// Diner consent: the cookie codec and its Google Consent Mode v2 mapping.
// The cookie is first-party per tenant host, so choices never cross tenants.

export const CONSENT_COOKIE = "itd_consent"
/** ~6 months, after which the banner asks again. */
export const CONSENT_COOKIE_MAX_AGE = 15_552_000

/**
 * Cookie value: `v1.<analytics>.<ads>` with `1` granted, `0` denied. Exported
 * as a pattern source so the inline bootstrap (bootstrap.ts) parses the cookie
 * with the exact same rule before React has loaded.
 */
export const CONSENT_VALUE_PATTERN = "v1\\.([01])\\.([01])"
const CONSENT_VALUE_RE = new RegExp(`^${CONSENT_VALUE_PATTERN}$`)

export interface ConsentChoice {
  analytics: boolean
  ads: boolean
}

export const ACCEPT_ALL: ConsentChoice = { analytics: true, ads: true }
export const REJECT_ALL: ConsentChoice = { analytics: false, ads: false }

export type ConsentState = "granted" | "denied"

export interface ConsentModeParams {
  ad_storage: ConsentState
  ad_user_data: ConsentState
  ad_personalization: ConsentState
  analytics_storage: ConsentState
}

export const DEFAULT_CONSENT_MODE: ConsentModeParams = {
  ad_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
  analytics_storage: "denied",
}

export function toConsentMode(choice: ConsentChoice): ConsentModeParams {
  const ads: ConsentState = choice.ads ? "granted" : "denied"
  return {
    ad_storage: ads,
    ad_user_data: ads,
    ad_personalization: ads,
    analytics_storage: choice.analytics ? "granted" : "denied",
  }
}

export function serializeConsent(choice: ConsentChoice): string {
  return `v1.${choice.analytics ? 1 : 0}.${choice.ads ? 1 : 0}`
}

/** `null` for absent, malformed or unknown-version values (⇒ ask again). */
export function parseConsent(
  raw: string | null | undefined
): ConsentChoice | null {
  const match = raw ? CONSENT_VALUE_RE.exec(raw) : null
  return match ? { analytics: match[1] === "1", ads: match[2] === "1" } : null
}

/** Reads the choice out of a `document.cookie`-style header string. */
export function readConsentCookie(cookieHeader: string): ConsentChoice | null {
  for (const part of cookieHeader.split(";")) {
    const [name, ...rest] = part.trim().split("=")
    if (name === CONSENT_COOKIE) return parseConsent(rest.join("="))
  }
  return null
}

/** A `document.cookie` assignment string for the choice. */
export function consentCookieString(
  choice: ConsentChoice,
  options: { secure: boolean }
): string {
  return [
    `${CONSENT_COOKIE}=${serializeConsent(choice)}`,
    "path=/",
    `max-age=${CONSENT_COOKIE_MAX_AGE}`,
    "samesite=lax",
    ...(options.secure ? ["secure"] : []),
  ].join("; ")
}
